import * as React from "react";
import * as Select from "@radix-ui/react-select";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, ChevronDown, Indent, Italic, List, ListOrdered, Outdent, TextQuote, Underline } from "lucide-react";
import { cn } from "@/lib/utils";
import { SelectItem } from "@/components/ui/select";

interface Props {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  height?: string;
}

// Restricted format surface: must stay within the allowlists of
// sanitizeSnapshotEventHtml (frontend) and pf.notifications.sanitize (backend emails).
const extensions = [
  StarterKit.configure({
    heading: { levels: [1, 2] },
    code: false,
    codeBlock: false,
    horizontalRule: false,
    link: false,
    strike: false,
  }),
];

const HEADING_OPTIONS = [
  { label: "Normal", value: "normal" },
  { label: "Heading 1", value: "1" },
  { label: "Heading 2", value: "2" },
];

interface ToolbarButtonProps {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const ToolbarButton = ({ label, active = false, disabled = false, onClick, children }: ToolbarButtonProps) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    aria-pressed={active}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "flex h-7 w-7 items-center justify-center rounded text-gray-600 transition-colors",
      active ? "bg-violet-100 text-violet-700" : "hover:bg-gray-100 hover:text-black",
      disabled && "pointer-events-none opacity-40",
    )}
  >
    {children}
  </button>
);

export const RichTextEditor: React.FC<Props> = ({ value, onChange, readOnly = false, height = "35rem" }) => {
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions,
    content: value,
    editable: !readOnly,
    onUpdate: ({ editor: e }) => onChangeRef.current(e.getHTML()),
    editorProps: {
      attributes: {
        class: "min-h-full px-4 py-3 text-sm outline-none",
      },
    },
  });

  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  React.useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [editor, readOnly]);

  const toolbarState = useEditorState({
    editor,
    selector: ({ editor: e }) =>
      e && {
        heading: e.isActive("heading", { level: 1 }) ? "1" : e.isActive("heading", { level: 2 }) ? "2" : "normal",
        bold: e.isActive("bold"),
        italic: e.isActive("italic"),
        underline: e.isActive("underline"),
        blockquote: e.isActive("blockquote"),
        orderedList: e.isActive("orderedList"),
        bulletList: e.isActive("bulletList"),
        canOutdent: e.can().liftListItem("listItem"),
        canIndent: e.can().sinkListItem("listItem"),
      },
  });

  if (!editor || !toolbarState) {
    return null;
  }

  const applyHeading = (selected: string) => {
    const chain = editor.chain().focus();
    if (selected === "normal") {
      chain.setParagraph().run();
    } else {
      chain.setHeading({ level: selected === "1" ? 1 : 2 }).run();
    }
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-md border border-gray-300 bg-white" style={{ height }}>
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-2 py-1.5">
        <Select.Root value={toolbarState.heading} onValueChange={applyHeading} disabled={readOnly}>
          <Select.Trigger
            aria-label="Text style"
            className="inline-flex h-7 w-32 items-center justify-between gap-1 rounded px-2 text-sm text-gray-700 outline-none hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-40"
          >
            <Select.Value />
            <Select.Icon>
              <ChevronDown size={14} />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal>
            <Select.Content
              position="popper"
              className="z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
            >
              <Select.Viewport className="p-1">
                {HEADING_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>

        <div className="flex items-center gap-0.5">
          <ToolbarButton
            label="Bold"
            active={toolbarState.bold}
            disabled={readOnly}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Italic"
            active={toolbarState.italic}
            disabled={readOnly}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Underline"
            active={toolbarState.underline}
            disabled={readOnly}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <Underline size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Blockquote"
            active={toolbarState.blockquote}
            disabled={readOnly}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <TextQuote size={16} />
          </ToolbarButton>
        </div>

        <div className="flex items-center gap-0.5">
          <ToolbarButton
            label="Ordered list"
            active={toolbarState.orderedList}
            disabled={readOnly}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Bullet list"
            active={toolbarState.bulletList}
            disabled={readOnly}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Decrease indent"
            disabled={readOnly || !toolbarState.canOutdent}
            onClick={() => editor.chain().focus().liftListItem("listItem").run()}
          >
            <Outdent size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Increase indent"
            disabled={readOnly || !toolbarState.canIndent}
            onClick={() => editor.chain().focus().sinkListItem("listItem").run()}
          >
            <Indent size={16} />
          </ToolbarButton>
        </div>
      </div>

      <EditorContent
        editor={editor}
        className={cn(
          "flex-1 cursor-text overflow-y-auto",
          "[&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-semibold",
          "[&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6",
          "[&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:text-gray-600",
        )}
      />
    </div>
  );
};
